import { Separator } from '@/components/ui/separator'
import ProTip from '../ProTip'

export default function TicketingSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        The ticketing system helps your team track customer issues, requests, and questions in one
        place. Whether a customer reports a problem or your team needs to escalate an internal
        request, tickets keep everything organized with clear ownership and status tracking.
      </p>

      <section id="creating-tickets" aria-labelledby="creating-tickets-heading">
        <h3 id="creating-tickets-heading" className="font-semibold mb-2">
          Creating a Ticket
        </h3>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Go to <span className="font-medium">Support &rarr; Tickets</span> and click{' '}
            <span className="font-medium">New Ticket</span>
          </li>
          <li>Enter a clear subject line that summarizes the issue</li>
          <li>
            Set the <span className="font-medium">priority</span> — Low, Medium, High, or Urgent
          </li>
          <li>Assign it to a team member (or leave it unassigned for the queue)</li>
          <li>Link it to an account or contact for context</li>
          <li>Add a description with any relevant details</li>
          <li>
            Click <span className="font-medium">Create</span>
          </li>
        </ol>
      </section>
      <Separator />

      <section id="managing-tickets" aria-labelledby="managing-tickets-heading">
        <h3 id="managing-tickets-heading" className="font-semibold mb-2">
          Managing Tickets
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Track each ticket's progress through these stages:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">New</span> — Just created, not yet picked up
          </li>
          <li>
            <span className="font-medium">Open</span> — Someone is actively working on it
          </li>
          <li>
            <span className="font-medium">Pending</span> — Waiting for a customer response or
            external input
          </li>
          <li>
            <span className="font-medium">On Hold</span> — Paused deliberately
          </li>
          <li>
            <span className="font-medium">Escalated</span> — Raised to a higher priority or team for
            further attention
          </li>
          <li>
            <span className="font-medium">Resolved</span> — The issue has been addressed, awaiting
            confirmation
          </li>
          <li>
            <span className="font-medium">Closed</span> — Done and dusted
          </li>
        </ul>
        <p className="text-sm text-muted-foreground mt-3 mb-2">
          <span className="font-medium">Who sees which tickets:</span> the support team and admins
          see all tickets. Everyone else sees tickets they <em>own or created</em> — so when Support
          takes over a request you submitted, it stays in your list, in the account's Tickets tab,
          and in your stat tiles all the way through Resolved and Closed. On the Tickets page,{' '}
          <span className="font-medium">My Tickets</span> shows tickets you own, while{' '}
          <span className="font-medium">All Tickets</span> also includes ones you created.
        </p>
        <p className="text-sm text-muted-foreground mt-3 mb-2">
          <span className="font-medium">Reassignment notifications:</span> When a ticket is
          reassigned to a different owner, the new owner receives both an in-app notification and an
          email with the ticket details and a direct link. This applies to individual reassignments
          and bulk updates.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Jira integration:</span> Tickets include an optional Jira
          field where you can enter a Jira ticket number (e.g., GRACKLE-123) to link the CRM ticket
          to your project tracking system.
        </p>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">SLA tracking:</span> Tickets are monitored against service
          level agreements so your team can prioritize effectively and meet response time
          commitments.
        </p>
      </section>
      <Separator />

      <section id="editing-tickets" aria-labelledby="editing-tickets-heading">
        <h3 id="editing-tickets-heading" className="font-semibold mb-2">
          Editing Tickets
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          You can edit all ticket fields directly from the detail page:
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Open a ticket and click the <span className="font-medium">Edit</span> button in the
            header
          </li>
          <li>
            Update any field — subject, description, status, priority, category, channel, account,
            contact, or owner
          </li>
          <li>The contact list filters automatically based on the selected account</li>
          <li>
            Click <span className="font-medium">Save</span> to apply your changes, or{' '}
            <span className="font-medium">Cancel</span> to discard
          </li>
        </ol>
      </section>
      <Separator />

      <section id="ticket-comments" aria-labelledby="ticket-comments-heading">
        <h3 id="ticket-comments-heading" className="font-semibold mb-2">
          Comments
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Inside a ticket, you can add <span className="font-medium">comments</span> to communicate
          with your team. Comment types include notes, call logs (with duration tracking), and email
          records.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Who can comment:</span> the support team and admins can
          comment on any ticket. If you <em>created or own</em> a ticket — for example an Internal
          Support request like a Trial Request — you can add comments to it too, even after Support
          takes ownership. Use this to add ongoing notes or context to your own requests.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Edit and delete:</span> You can edit or delete your own
          comments using the dropdown menu on each comment card (admins can manage any comment).
          Edited comments show an &ldquo;(edited)&rdquo; indicator. Deleted comments are
          soft-deleted and can be restored by an administrator.
        </p>
      </section>
      <Separator />

      <section id="ticket-attachments" aria-labelledby="ticket-attachments-heading">
        <h3 id="ticket-attachments-heading" className="font-semibold mb-2">
          Attachments
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Attach files to tickets to provide supporting documentation, screenshots, or logs. The
          Attachments tab on the ticket detail page shows all files associated with the ticket.
        </p>
        <p className="text-sm text-muted-foreground">
          When submitting a bug report through the Internal Support form, you can attach files
          directly with the submission. These files are linked to the automatically created ticket.
        </p>
      </section>
      <Separator />

      <section id="ticket-tasks" aria-labelledby="ticket-tasks-heading">
        <h3 id="ticket-tasks-heading" className="font-semibold mb-2">
          Tasks on Tickets
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Create follow-up tasks directly from a ticket without leaving the page. The Tasks tab on
          the ticket detail page lets you track action items related to the ticket.
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Open a ticket and click the <span className="font-medium">Tasks</span> tab
          </li>
          <li>Fill in the subject, due date, priority, and an optional description</li>
          <li>
            Click <span className="font-medium">Create Task</span>
          </li>
        </ol>
        <p className="text-sm text-muted-foreground mt-2">
          Tasks appear in a list below the form, showing status, due date, owner, and priority.
          Click a task subject to view or edit it. Support users can create and view tasks on their
          tickets without needing access to the main Tasks page.
        </p>
      </section>
      <Separator />

      <section id="ticket-filtering" aria-labelledby="ticket-filtering-heading">
        <h3 id="ticket-filtering-heading" className="font-semibold mb-2">
          Filtering and Searching Tickets
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          The tickets list page offers several ways to find what you need:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            <span className="font-medium">Multi-select status filter</span> — Select one or more
            statuses (New, Open, Pending, On Hold, Escalated, Resolved, Closed) to narrow the list
          </li>
          <li>
            <span className="font-medium">Search by field</span> — Choose a specific field to search
            (ticket number, subject, description, account name, or contact name) for more targeted
            results
          </li>
          <li>
            <span className="font-medium">Priority filter</span> — Filter by Low, Medium, High, or
            Urgent
          </li>
          <li>
            <span className="font-medium">Sortable columns</span> — Click any column header to sort;
            click again to reverse the order
          </li>
        </ul>
      </section>
      <Separator />

      <ProTip>
        <span className="font-medium">Task vs. ticket?</span> Use{' '}
        <span className="font-medium">tasks</span> for internal follow-ups and sales activities
        (e.g., "Send proposal to client"). Use <span className="font-medium">tickets</span> for
        customer-reported issues that need tracking and resolution (e.g., "Customer cannot access
        their invoice"). You can also create tasks <em>on</em> a ticket for follow-up items that
        need to happen before the ticket can be resolved.
      </ProTip>

      <section id="internal-support" aria-labelledby="internal-support-heading">
        <h3 id="internal-support-heading" className="font-semibold mb-2">
          Internal Support Requests
        </h3>
        <p className="text-sm text-muted-foreground mb-2">
          Use the <span className="font-medium">Internal Support</span> form (under Support in the
          sidebar) to submit requests directly within the CRM. This replaces the external Google
          Form and supports 13 request types:
        </p>
        <ul
          className="list-disc list-inside space-y-1 text-sm text-muted-foreground ml-2 mb-2"
          role="list"
        >
          <li>Request System Access</li>
          <li>New Employee Onboarding</li>
          <li>Feature Request</li>
          <li>Report a Bug (with file attachment support)</li>
          <li>Request a Change</li>
          <li>Dashboard Account</li>
          <li>Security Questionnaires</li>
          <li>Trial Request (including an optional Notes field for extra context)</li>
          <li>Marketing Feedback / Suggestion</li>
          <li>GPO Client Feedback</li>
          <li>Scan Request</li>
          <li>Trial Scan</li>
          <li>Shipping Label</li>
        </ul>
        <p className="text-sm text-muted-foreground">
          Each request type shows the fields most relevant to that request. When you submit, a
          ticket is automatically created with a formatted description so the support team can begin
          working on it immediately. Your submitted requests stay visible to you in Tickets, and you
          can add follow-up comments to them at any time.
        </p>
      </section>
      <Separator />

      <ProTip>
        <span className="font-medium">Create contacts on the fly:</span> When creating a ticket for
        a new customer, click the <span className="font-medium">+</span> button next to the Contact
        dropdown to create a new contact without leaving the ticket form. If you have already
        selected an account, it is pre-populated in the contact creation modal.
      </ProTip>

      <p className="text-sm text-muted-foreground mt-2">
        <span className="font-medium">Note:</span> Support users have access only to the ticketing
        system. This is their dedicated workspace for managing customer requests, including creating
        tasks on tickets.
      </p>
    </>
  )
}
